import { useMemo } from 'react';

import {
  type PropertySchemaMap,
  usePropertySchema,
} from '@/lib/crm/hooks/usePropertySchema';

import type { AhoyPropertyOption } from '../types';

export function useHubspotAhoyProperties(orgId: number | string | undefined) {
  const { schema: contact } = usePropertySchema(orgId ?? '', 'ahoy_contact');
  const { schema: company } = usePropertySchema(orgId ?? '', 'ahoy_company');
  const { schema: deal } = usePropertySchema(orgId ?? '', 'ahoy_deal');

  return useMemo((): Record<
    'contact' | 'company' | 'deal',
    AhoyPropertyOption[]
  > => {
    const toOptions = (schema: PropertySchemaMap): AhoyPropertyOption[] =>
      Object.values(schema ?? {})
        // A mapped property must be both create-writable and update-writable:
        // sync applies inbound/outbound updates, which are rejected when
        // value_update_access is 'none' (create-only properties).
        .filter(
          p => p.value_write_access === 'all' && p.value_update_access === 'all'
        )
        .map(p => ({ name: p.property_type_name, label: p.label }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return {
      contact: toOptions(contact),
      company: toOptions(company),
      deal: toOptions(deal),
    };
  }, [contact, company, deal]);
}
